import React, { Fragment } from "react";
import { Link } from "gatsby";
import PropTypes from "prop-types";
import styled from "styled-components";

import { Image } from "../../globals/UIKit";
import CopyWriter from "./CopyWriter";
import StyledSectionWrapper from "../shared/StyledSectionWrapper";
import StyledContainerWrapper from "../shared/StyledContainerWrapper";
import { StyledDesc2, StyledDesc3 } from "../shared/StyledTypography";
import { COLORS } from "../../globals/designSystem";
import { mq } from "../../globals/utils";
import { removePaddBottom, removePadd, mobileContainerWrapper } from "../shared/CommonStyled";

// import CookiesPrompt from "./cookiesprompt";
import SubscribeNewsletter from "./subscribenewsletter";
import hasuraLogoColor from "../../images/hasura-logo-color.svg";
import hasuraLogoWhite from "../../images/hasura-logo-white.svg";
import { HASURA_HUB_URL } from "../../utils/constants";

const scrollToTop = () => {
  if (typeof window !== undefined) {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }
};

const footerListState = [
  {
    title: "Product",
    links: [
      {
        linkContent: "Hasura CE",
        linkUrl: "/opensource/",
      },
      {
        linkContent: "Hasura Cloud",
        linkUrl: "/cloud/",
      },
      {
        linkContent: "Hasura EE",
        linkUrl: "/enterprise/",
      },
      {
        linkContent: "Security",
        linkUrl: "/security/",
      },
      {
        linkContent: "Pricing",
        linkUrl: "/pricing/",
      },
      {
        linkContent: "Contact Sales",
        linkUrl: "/contact-us/",
      },
    ],
  },
  {
    title: "Resources",
    links: [
      {
        linkContent: "Blog",
        linkUrl: "https://hasura.io/blog/",
        externalLink: true,
      },
      {
        linkContent: "User stories",
        linkUrl: "/case-studies/",
      },
      {
        linkContent: "Tools",
        linkUrl: "/tools/",
      },
      {
        linkContent: "Reference App",
        linkUrl: "/reference-app/",
      },
      {
        linkContent: "Hasura Hub",
        linkUrl: HASURA_HUB_URL,
      },
      {
        linkContent: "GraphiQL",
        linkUrl: "https://cloud.hasura.io/public/graphiql",
        externalLink: true,
      },
      {
        linkContent: "Changelog",
        linkUrl: "https://github.com/hasura/graphql-engine/releases/",
        externalLink: true,
      },
      {
        linkContent: "Sample Apps",
        linkUrl: "/sample-apps/",
      },
    ],
  },
  {
    title: "Community",
    links: [
      {
        linkContent: "Community",
        linkUrl: "/community/",
      },
      {
        linkContent: "Learn",
        linkUrl: "/learn/",
      },
      {
        linkContent: "GraphQL with Hasura",
        linkUrl: "/graphql/",
      },
      {
        linkContent: "Conferences",
        linkUrl: "/conferences/",
      },
      {
        linkContent: "Community Call",
        linkUrl: "/community/community-call/",
      },
      {
        linkContent: "Events",
        linkUrl: "/events/",
      },
      {
        linkContent: "Swag Store",
        linkUrl: "https://store.hasura.io/",
        externalLink: true,
      },
    ],
  },
  {
    title: "Support",
    links: [
      {
        linkContent: "Docs",
        linkUrl: "https://hasura.io/docs/latest/index/",
        externalLink: true,
      },
      {
        linkContent: "Help",
        linkUrl: "/help/",
      },
      {
        linkContent: "Github",
        linkUrl: "https://github.com/hasura",
        externalLink: true,
      },
    ],
  },
  {
    title: "Hasura",
    links: [
      {
        linkContent: "About Us",
        linkUrl: "/about/",
      },
      {
        linkContent: "Press",
        linkUrl: "/press/",
      },
      {
        linkContent: "Careers",
        linkUrl: "/careers/",
        linkTag: "We’re hiring",
      },
      {
        linkContent: "Legal Stuff",
        linkUrl: "/legal/",
      },
    ],
  },
];

const StyledSectionWrapperRel = styled(StyledSectionWrapper)`
  position: relative;
  .subLeft,
  .subRight {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
  }
  .subLeft {
    left: 0;
  }
  .subRight {
    right: 0;
  }
  ${mq.between("lg", "xl")} {
    .subLeft {
      max-height: 140px;
    }
    .subRight {
      max-height: 140px;
    }
  }
  ${mq.between("md", "lg")} {
    .subRight {
      display: none;
    }
    .subLeft {
      position: static;
      transform: none;
    }
  }
  ${mq.below.md} {
    .subRight {
      display: none;
    }
    .subLeft {
      position: static;
      transform: none;
    }
  }
`;

const StyledNewsletterWrapper = styled.div`
  max-width: 830px;
  margin: 0 auto;
  padding-bottom: 64px;
  ${mq.between("lg", "xl")} {
    max-width: 750px;
  }
  ${mq.below.md} {
    padding-bottom: 50px;
  }
`;

const StyledFooterLinksWrapper = styled.div`
  .footerLinksWrapper {
    display: flex;
    justify-content: space-between;
    .links {
      display: block;
      margin-bottom: 12px;
      a {
        color: ${COLORS.neutral_60};
        &:hover {
          color: ${COLORS.neutral_82};
        }
        .mr16 {
          margin-right: 16px;
        }
        .linkTag {
          background-color: ${COLORS.green_10};
          padding: 4px 8px;
          border-radius: 4px;
          color: ${COLORS.green_60};
        }
      }
      &:last-child {
        margin-bottom: 0;
      }
    }
  }
  ${mq.below.md} {
    border-top: 0;
    .footerLinksWrapper {
      padding-top: 0;
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
      grid-gap: 40px 32px;
      .links {
        a {
          .mr16 {
            margin-right: 8px;
          }
          .linkTag {
            padding: 2px 4px;
          }
        }
      }
    }
  }
  @media (min-width: 548px) and (max-width: 555px) {
    .footerLinksWrapper {
      grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    }
  }
  @media (max-width: 378px) {
    .footerLinksWrapper {
      grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
      a {
        .linkTag {
          display: inline-grid;
          margin-top: 5px;
        }
      }
    }
  }
`;

const StyledFooterWrapper = styled.footer`
  .footerGridWrapper {
    border-top: 1px solid ${COLORS.neutral_12};
    padding: 80px 0 50px 0;
    display: grid;
    grid-template-columns: 1fr 3fr;
    .brand {
      img {
        width: 130px;
      }
    }
  }
  ${mq.below.xl} {
    .footerGridWrapper {
      grid-template-columns: 1fr;
      grid-gap: 40px;
    }
  }
`;

const Footer = props => {
  const path = props.location.pathname;

  const isHasuraConf = path.startsWith("/events/hasura-con-2020/") ? true : false;
  const isRemoveNewsletterImg = path === "/learn/" || path === "/graphql/" ? true : false;
  // const isHome = path === "/" ? true : false;

  const isNewsletterHide =
    path.startsWith("/store") ||
    path === "/store/" ||
    path === "/store" ||
    (path.startsWith("/graphql/database") && path !== "/graphql/database/postgresql/") ||
    path.startsWith("/rest-api/database")
      ? true
      : false;

  // const utmPagePathName = getUTMPagePathName(path);

  const isStorePage = path.startsWith("/store");

  const getFooterLinksList = () => {
    if (props?.isStorePage || isStorePage) {
      return null;
    }

    return (
      <StyledFooterLinksWrapper>
        <div className="footerLinksWrapper">
          {footerListState.map((footer, index) => (
            <div className="footerLinks" key={index}>
              <StyledDesc2 variant="neutral_82" paddingBottom="pb16" fontWeight="font_600">
                {footer.title}
              </StyledDesc2>
              {footer.links.map((link, subIndex) => (
                <StyledDesc3 className="links" fontWeight="font_400" key={subIndex}>
                  {link.externalLink ? (
                    <a href={link.linkUrl}>
                      <span className="mr16">{link.linkContent}</span>
                      {link.linkTag && <span className="linkTag">{link.linkTag}</span>}
                    </a>
                  ) : (
                    <a href={link.linkUrl} onClick={scrollToTop}>
                      <span className="mr16">{link.linkContent}</span>
                      {link.linkTag && <span className="linkTag">{link.linkTag}</span>}
                    </a>
                  )}
                </StyledDesc3>
              ))}
            </div>
          ))}
        </div>
      </StyledFooterLinksWrapper>
    );
  };

  return (
    <Fragment>
      {!isHasuraConf && (
        <Fragment>
          {!isNewsletterHide && (
            <StyledSectionWrapperRel id="footer-newsletter" css={removePaddBottom}>
              {!isRemoveNewsletterImg && (
                <Image
                  className="subLeft"
                  src="https://graphql-engine-cdn.hasura.io/assets/main-site/subscribe-left.png"
                  alt="Subscribe Illu"
                />
              )}
              {!isRemoveNewsletterImg && (
                <Image
                  className="subRight"
                  src="https://graphql-engine-cdn.hasura.io/assets/main-site/subscribe-right.png"
                  alt="Subscribe Illu"
                />
              )}
              <StyledContainerWrapper css={props.learnPage ? removePadd : null}>
                <div css={props.learnPage ? mobileContainerWrapper : null}>
                  <StyledNewsletterWrapper>
                    <SubscribeNewsletter props={props} footerNewsletter={true} />
                  </StyledNewsletterWrapper>
                </div>
              </StyledContainerWrapper>
            </StyledSectionWrapperRel>
          )}
          <StyledFooterWrapper
            className={
              "wd100" +
              (isHasuraConf ? " hasuraConfBgColor" : props?.isStorePage ? " hasuraSwagStore" : "")
            }
          >
            <StyledContainerWrapper css={props.learnPage ? removePadd : null}>
              <div
                className="footerGridWrapper"
                css={props.learnPage ? mobileContainerWrapper : null}
              >
                <div className="brand">
                  <a href="/" onClick={scrollToTop}>
                    <Image
                      src={props.isDarkMode ? hasuraLogoWhite : hasuraLogoColor}
                      alt="Brand logo"
                    />
                  </a>
                </div>
                {getFooterLinksList()}
              </div>
            </StyledContainerWrapper>
            <CopyWriter isDarkMode={props?.isStorePage || false} />
            {/* <CookiesPrompt /> */}
          </StyledFooterWrapper>
        </Fragment>
      )}
    </Fragment>
  );
};

Footer.propTypes = {
  location: PropTypes.object,
};

export default Footer;
